import React, { useState, useEffect, useRef } from "react";
import { Input } from "ui-components";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";

/**
 * Individual number box
 */
const NumberBox = props => {
  const {
    updateValidity,
    defaultValue = "",
    clearBox = false,
    onPressEnter,
    autoFocus,
    onPaste,
    otpInputValue,
    boxIndex
  } = props;
  const [value, setValue] = useState(isNil(defaultValue) ? "" : defaultValue);
  const boxRef = useRef(null);

  useEffect(() => {
    setValue(isNil(otpInputValue) ? "" : otpInputValue);
  }, [otpInputValue]);

  useEffect(() => {
    if (clearBox) setValue(isNil(defaultValue) ? "" : defaultValue);
  }, [clearBox, defaultValue]);

  useEffect(() => {
    if (boxRef?.current && autoFocus) {
      boxRef.current.focus();
    }
  }, [boxRef, autoFocus]);
  /**
   * Jump to next available sibling and trigger a focus
   * @param {HTMLElement} target event triggered element
   */
  const focusOnNextBox = target => {
    const nextSibling = target.nextElementSibling;
    if (nextSibling === null) return;

    nextSibling.focus();
  };
  /**
   * Jump to previous sibling (if available) and trigger a focus
   * @param {HTMLElement} target event triggered element
   */
  const focusOnPrevBox = target => {
    const prevSibling = target.previousElementSibling;
    if (prevSibling === null) return;
    // setting cursor at the end of input value
    const end = prevSibling.value?.length || 0;
    prevSibling.setSelectionRange(end, end);
    prevSibling.focus();
  };
  /**
   * Input on change handler
   * This will set the value only if it is an integer.
   * Everyother input will not be accepted as the value.
   * @param {KeyboardEvent} event
   */
  const onChange = ({ target }) => {
    const newValue = target.value;
    const numValue = Number.parseInt(newValue);

    if (Number.isInteger(numValue)) {
      setValue(numValue);
      updateValidity(true, numValue);
      return focusOnNextBox(target);
    } else {
      setValue("");
      updateValidity(false);
    }
  };
  /**
   * Input keydown handler
   * This is registered to capture the backspace key
   * When user makes a backspace from an empty input it will
   * jump into previous sibling
   * @param {KeyboardEvent} event
   */
  const onKeyDown = ({ keyCode, target }) => {
    const BACKSPACE_KEY_CODE = 8;
    const ENTER_KEY_CODE = 13;

    // jump to previous only if previous value is empty.
    if (keyCode === BACKSPACE_KEY_CODE && value === "") {
      focusOnPrevBox(target);
    } else if (keyCode === ENTER_KEY_CODE) {
      isFunction(onPressEnter) && onPressEnter();
    }
  };

  return (
    <Input
      onChange={onChange}
      onKeyDown={onKeyDown}
      ref={boxRef}
      value={value}
      type="tel"
      min="0"
      max="9"
      maxLength={1}
      textAlign="center"
      data-testid="number-box"
      data-boxindex={boxIndex}
      onPaste={onPaste}
    />
  );
};

export default NumberBox;
